<script setup lang="ts">
import MainLayout from '@/common/components/MainLayout.vue';
import { useDashboardStore } from '@/dashboards/stores/dashboardStore';
import { useRefreshDashboardStore } from '@/dashboards/stores/refreshDashboardStore';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { onMounted, ref } from 'vue';
import { Card } from 'primevue';
import DashboardView from './components/DashboardView.vue';
import { Select, InputGroup, InputGroupAddon } from 'primevue';
import { useSortByName } from '@/common/composables/sortByName';
import { storeToRefs } from 'pinia';
import type { Dashboard } from '@/dashboards/domain/dashboard';
import { useLocalStorage } from '@vueuse/core';

const dashboardStore = useDashboardStore();
const refreshDashboardStore = useRefreshDashboardStore();
const dashboardSetting = useLocalStorage('dashboard', '');

const dashboards = useSortByName({
  list: storeToRefs(dashboardStore).list,
  getName: (d) => d.name,
});

const selectedDashboard = ref<Dashboard>();

const translations = useTranslations();

onMounted(async () => {
  await refreshDashboardStore.initialize();
  if (dashboards.value.length === 0) return;

  const dashboardId = dashboardSetting.value;
  if (dashboardId !== '') {
    selectedDashboard.value = dashboards.value.find((d) => d.id === dashboardId);
    if (selectedDashboard.value) return;
  }
  selectedDashboard.value = dashboards.value[0];
});

function onSelectedDashboardChanged(dashboard: Dashboard) {
  dashboardSetting.value = dashboard.id;
}
</script>

<template>
  <MainLayout>
    <Card class="overview-card">
      <template #title> </template>
      <template #content>
        <span class="title">{{ translations.overview.title }}</span>
        <InputGroup class="dashboard-select" v-if="dashboards.length > 0">
          <InputGroupAddon>
            <i class="pi pi-chart-bar"></i>
          </InputGroupAddon>
          <Select
            fluid
            style="min-width: 200px"
            :options="dashboards"
            v-model:model-value="selectedDashboard"
            option-label="name"
            @change="(ev) => onSelectedDashboardChanged(ev.value)"
          >
          </Select>
        </InputGroup>
        <DashboardView
          class="dashboard-grid"
          v-if="selectedDashboard"
          v-model:dashboard="selectedDashboard"
        ></DashboardView>
      </template>
    </Card>
  </MainLayout>
</template>

<style scoped>
:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto;
  column-gap: 0.5rem;
  align-items: center;
}

.dashboard-select {
  grid-column: 3;
  grid-row: 1;
  min-width: 100px;
}

.overview-card {
  height: 100%;
  overflow-y: hidden;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
  display: grid;
  row-gap: 0.5rem;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto 1fr;
}

.title {
  grid-row: 1;
  grid-column: 1;
  font-size: var(--text-lg);
  font-weight: 600;
  align-self: center;
}

.dashboard-grid {
  grid-row: 2;
  grid-column: 1/4;
}
</style>
